// Imports
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import styled from 'styled-components/macro';
import Button from '../../../../../../../shareables/foundation/front-end/components/buttons/standard';
import { useAppDispatch, useAppSelector } from '../../../../../../../shareables/foundation/front-end/redux/hooks';
import apiActions from '../../../../../../../shareables/integrations/api/redux/actions';
import { GetAddressesResponse } from '../../../../../../../shareables/types/api/main/website/account/addresses/get';
import { GetRegistrationResponse } from '../../../../../../../shareables/types/api/main/website/settings/registration/get';
import Address from './address';
import UpsertAddress from './upsert-address';


// Styled components
const Container = styled.div`
	line-height: 1.3125rem;
`;

const Columns = styled.div`
	display: flex;
	flex-wrap: wrap;
	margin: -0.75rem;
`;

const AddNewAddressButton = styled(Button)`
	margin-top: 1.5rem;
`;


// Define the accepted props
interface Props {
	storybookAddresses?: GetAddressesResponse;
	storybookRegistrationSettings?: GetRegistrationResponse;
}


// Function component
const Addresses: React.FC<Props> = ({ storybookAddresses, storybookRegistrationSettings }) => {
	// Use state
	const [addresses, setAddresses] = useState<GetAddressesResponse | null>(storybookAddresses ?? null);
	const [showUpsertAddress, setShowUpsertAddress] = useState(Boolean(storybookRegistrationSettings));
	const [currentAddressID, setCurrentAddressID] = useState<GetAddressesResponse[0]['id']>();
	
	
	// Use Redux functionality
	const dispatch = useAppDispatch();
	const accountType = useAppSelector((state) => state.self.member?.account.type);
	
	
	// Use ref
	const ref = useRef<HTMLDivElement | null>(null);
	
	
	// Use React Router functionality
	const updateHeight: (newHeight: number) => void = useOutletContext();
	
	
	// Update height for the members settings page
	useEffect(() => {
		if (updateHeight instanceof Function && ref.current) {
			updateHeight(ref.current.clientHeight);
		}
	}, [updateHeight, addresses, storybookAddresses, storybookRegistrationSettings]);
	
	
	// Function to load addresses
	const memoizedLoadAddresses = useCallback(() => {
		void dispatch(
			apiActions.call<undefined, undefined, GetAddressesResponse>({
				action: 'GET',
				uri: `/website/${window.location.hostname}/account/addresses`,
				completion: (json) => {
					setAddresses(json.response);
				},
			})
		);
	}, [dispatch]);
	
	
	// Load addresses on mount
	useEffect(() => {
		if (accountType?.addressRequirements !== 'NONE') {
			memoizedLoadAddresses();
		}
	}, [memoizedLoadAddresses, accountType?.addressRequirements]);
	
	
	// Handle change
	const handleChange = useCallback(() => {
		memoizedLoadAddresses();
		setShowUpsertAddress(false);
		setCurrentAddressID(undefined);
	}, [memoizedLoadAddresses]);
	
	
	// If loading, then display nothing
	if (addresses === null && accountType?.addressRequirements !== 'NONE') {
		return null;
	}
	
	
	// Handle close
	const handleClose = () => {
		setShowUpsertAddress(false);
		setCurrentAddressID(undefined);
	};
	
	
	// Handle click
	const handleEditClick = (addressID: GetAddressesResponse[0]['id']) => {
		setCurrentAddressID(addressID);
		setShowUpsertAddress(true);
	};
	
	const handleAddClick = () => {
		setCurrentAddressID(undefined);
		setShowUpsertAddress(true);
	};
	
	
	// Return JSX
	return (
		<Container ref={ref}>
			{accountType?.addressRequirements === 'NONE' ? (
				<em>Your account type ({accountType.name}) does not need addresses.</em>
			) : (
				<React.Fragment>
					<UpsertAddress
						addressID={currentAddressID}
						onCompletion={handleChange}
						onClose={handleClose}
						isFormOpen={showUpsertAddress}
						storybookRegistrationSettings={storybookRegistrationSettings}
					/>
					
					{addresses && addresses.length > 0 && (
						<Columns>
							{addresses.map((address) => (
								<Address
									key={address.id}
									address={address}
									onChange={handleChange}
									onClick={() => handleEditClick(address.id)}
								/>
							))}
						</Columns>
					)}
					
					{!showUpsertAddress && (
						<AddNewAddressButton type='button' color='plain' border smaller onClick={handleAddClick}>
							<FontAwesomeIcon icon={faPlus} />
							<span>Add address</span>
						</AddNewAddressButton>
					)}
				</React.Fragment>
			)}
		</Container>
	);
};

export default Addresses;
